/**
 * Hook for canvas dimension management
 * Extracted from Canvas.tsx lines 138-287
 * Handles initial sizing, resize events, and play bar transitions
 */

import { type RefObject, useEffect, useState } from 'react'
import { useDimensions } from '@/hooks'
import { eventBus } from '@/services'
import type { CanvasDimensions } from '../BaseCanvas.types'

interface UseCanvasDimensionsOptions {
	whiteboardRef: RefObject<HTMLDivElement | null>
	showPlayBar: boolean
}

/**
 * Manages canvas dimensions with automatic updates on:
 * - Container resize (via ResizeObserver in useDimensions)
 * - Window resize events (via eventBus)
 * - Play bar show/hide transitions
 */
export function useCanvasDimensions({
										whiteboardRef,
										showPlayBar,
									}: UseCanvasDimensionsOptions): CanvasDimensions {
	// Use shared hook for core ResizeObserver functionality
	// Uses clientWidth/Height to match FootballField measurements (excludes border)
	const baseDimensions = useDimensions(whiteboardRef, { method: 'client' })

	// Local state for eventBus-triggered updates
	const [eventDimensions, setEventDimensions] = useState<CanvasDimensions | null>(null)

	// Handle system resize events from eventBus
	useEffect(() => {
		function handleResize() {
			if (!whiteboardRef.current) return
			const rect = whiteboardRef.current.getBoundingClientRect()
			setEventDimensions({
				width: rect.width,
				height: rect.height,
			})
		}

		eventBus.on('system:resize', handleResize)
		return () => eventBus.off('system:resize', handleResize)
	}, [whiteboardRef])

	// Clear eventDimensions when base dimensions update
	// This ensures ResizeObserver updates take precedence
	useEffect(() => {
		setEventDimensions(null)
	}, [baseDimensions])

	// Return eventBus dimensions if available, otherwise base dimensions
	return eventDimensions ?? baseDimensions
}
